<script setup>
/* eslint-env browser */
/* global FB */
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { loadScript } from 'dashboard/helper/DOMHelpers';
import ChannelApi from '../../../../../api/channels';
import SettingsSection from '../../../../../components/SettingsSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import * as Sentry from '@sentry/vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const FB_COMMENT_SCOPE =
  'pages_manage_metadata,pages_show_list,pages_read_engagement,pages_manage_engagement';
const LINK_PATTERN = /https?:\/\/|www\./i;

const { t } = useI18n();
const store = useStore();
const { accountId } = useAccount();
const inboxes = useMapGetter('inboxes/getInboxes');
const isDev = import.meta.env.DEV;

const isConnecting = ref(false);
const isSaving = ref(false);
const connectError = ref('');
const enabled = ref(false);
const template = ref('');
const justConnected = ref(false);

const commentInbox = computed(() =>
  inboxes.value.find(
    i =>
      i.channel_type === 'Channel::FacebookPageComment' &&
      i.page_id === props.inbox.page_id
  )
);
const isConnected = computed(() => !!commentInbox.value);
const templateHasLink = computed(() => LINK_PATTERN.test(template.value));
const pageAvatarInitial = computed(() =>
  (props.inbox.name || '?').trim().charAt(0).toUpperCase()
);
const NAME_TOKEN = /(\{\{\s*name\s*\}\})/g;
const previewPublicReplyParts = computed(() => {
  if (!template.value) {
    return [
      {
        text: t('INBOX_MGMT.COMMENT_AUTO_REPLY.PREVIEW.PUBLIC_PLACEHOLDER'),
        isName: false,
      },
    ];
  }
  const sampleName = t('INBOX_MGMT.COMMENT_AUTO_REPLY.PREVIEW.SAMPLE_NAME');
  return template.value
    .split(NAME_TOKEN)
    .filter(part => part !== '')
    .map(part => {
      const isName = /\{\{\s*name\s*\}\}/.test(part);
      return { text: isName ? sampleName : part, isName };
    });
});

watch(
  commentInbox,
  inbox => {
    if (!inbox) return;
    enabled.value = !!inbox.provider_config?.auto_reply_enabled;
    template.value = inbox.provider_config?.public_reply_template || '';
  },
  { immediate: true }
);

const runFBInit = () => {
  FB.init({
    appId: window.chatwootConfig.fbAppId,
    xfbml: true,
    version: window.chatwootConfig.fbApiVersion,
    status: true,
  });
  window.fbSDKLoaded = true;
};

onMounted(() => {
  window.fbAsyncInit = runFBInit;
});

const insertNameVar = () => {
  template.value = `${template.value}{{name}} `;
};

const chooseInitialState = enable => {
  enabled.value = enable;
  justConnected.value = false;
};

const createCommentChannel = async ({
  user_access_token: userAccessToken,
  page_access_token: pageAccessToken,
  page_id: pageId,
}) => {
  await store.dispatch('inboxes/createFBCommentChannel', {
    user_access_token: userAccessToken,
    page_access_token: pageAccessToken,
    page_id: pageId,
    inbox_name: `${props.inbox.name} Comments`,
  });
  justConnected.value = true;
};

const fetchAndCreate = async token => {
  try {
    const response = await ChannelApi.fetchFacebookPages(
      token,
      accountId.value
    );
    const { data } = response.data;
    const page = (data.page_details || []).find(
      p => p.id === props.inbox.page_id
    );
    if (!page) {
      isConnecting.value = false;
      connectError.value = t(
        'INBOX_MGMT.COMMENT_AUTO_REPLY.CONNECT.PAGE_NOT_FOUND'
      );
      return;
    }
    await createCommentChannel({
      user_access_token: data.user_access_token,
      page_access_token: page.access_token,
      page_id: page.id,
    });
  } catch (error) {
    connectError.value = t('INBOX_MGMT.DETAILS.ERROR_FB_AUTH');
  } finally {
    isConnecting.value = false;
  }
};

const tryFBLogin = () => {
  FB.login(
    response => {
      if (response.status === 'connected') {
        fetchAndCreate(response.authResponse.accessToken);
      } else {
        isConnecting.value = false;
        connectError.value = t('INBOX_MGMT.DETAILS.ERROR_FB_AUTH');
      }
    },
    { scope: FB_COMMENT_SCOPE }
  );
};

const connect = async () => {
  isConnecting.value = true;
  connectError.value = '';
  try {
    await loadScript('https://connect.facebook.net/en_US/sdk.js', {
      id: 'facebook-jssdk',
    });
    runFBInit();
    tryFBLogin();
  } catch (error) {
    isConnecting.value = false;
    Sentry.captureException(error);
    useAlert(t('INBOX_MGMT.DETAILS.ERROR_FB_LOADING'));
  }
};

// Dev-only: skips the real Facebook OAuth popup so the rest of the
// connect -> enable -> template flow can be reviewed locally without a
// live Facebook app/page. Never reachable in a production build because
// it is gated by Vite's import.meta.env.DEV, not by account or config.
const devSkipConnect = async () => {
  isConnecting.value = true;
  connectError.value = '';
  try {
    await createCommentChannel({
      user_access_token: `dev-local-${Date.now()}`,
      page_access_token: `dev-local-${Date.now()}`,
      page_id: props.inbox.page_id,
    });
  } catch (error) {
    connectError.value = t('INBOX_MGMT.DETAILS.ERROR_FB_AUTH');
  } finally {
    isConnecting.value = false;
  }
};

const save = async () => {
  if (templateHasLink.value) {
    useAlert(t('INBOX_MGMT.COMMENT_AUTO_REPLY.TEMPLATE.LINK_ERROR'));
    return;
  }
  try {
    isSaving.value = true;
    await store.dispatch('inboxes/updateInbox', {
      id: commentInbox.value.id,
      formData: false,
      channel: {
        provider_config: {
          auto_reply_enabled: enabled.value,
          public_reply_template: template.value,
        },
      },
    });
    useAlert(t('INBOX_MGMT.COMMENT_AUTO_REPLY.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.COMMENT_AUTO_REPLY.API.ERROR_MESSAGE'));
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <SettingsSection
    :title="$t('INBOX_MGMT.COMMENT_AUTO_REPLY.TITLE')"
    :sub-title="$t('INBOX_MGMT.COMMENT_AUTO_REPLY.SUBTITLE')"
  >
    <div v-if="!isConnected" class="flex flex-col items-start gap-3">
      <p v-if="connectError" class="text-sm text-n-ruby-9">
        {{ connectError }}
      </p>
      <p class="text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.COMMENT_AUTO_REPLY.CONNECT.PERMISSION_NOTE') }}
      </p>
      <NextButton
        :label="$t('INBOX_MGMT.COMMENT_AUTO_REPLY.CONNECT.BUTTON')"
        :is-loading="isConnecting"
        @click="connect"
      />
      <NextButton
        v-if="isDev"
        faded
        slate
        xs
        label="Skip Facebook auth (dev only)"
        :is-loading="isConnecting"
        @click="devSkipConnect"
      />
    </div>

    <div v-else-if="justConnected" class="flex flex-col items-start gap-3">
      <p class="text-sm font-medium text-n-slate-12">
        {{ $t('INBOX_MGMT.COMMENT_AUTO_REPLY.CONNECT.CONNECTED_TITLE') }}
      </p>
      <p class="text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.COMMENT_AUTO_REPLY.CONNECT.CONNECTED_QUESTION') }}
      </p>
      <div class="flex gap-2">
        <NextButton
          :label="$t('INBOX_MGMT.COMMENT_AUTO_REPLY.CONNECT.ENABLE_NOW')"
          @click="chooseInitialState(true)"
        />
        <NextButton
          faded
          slate
          :label="$t('INBOX_MGMT.COMMENT_AUTO_REPLY.CONNECT.DECIDE_LATER')"
          @click="chooseInitialState(false)"
        />
      </div>
    </div>

    <div
      v-else
      class="rounded-xl border border-n-weak bg-n-alpha-1 divide-y divide-n-weak overflow-hidden"
    >
      <div class="flex items-center justify-between px-4 py-3">
        <div>
          <p class="text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.COMMENT_AUTO_REPLY.ENABLE.LABEL') }}
          </p>
          <p class="text-xs text-n-slate-11">
            {{ $t('INBOX_MGMT.COMMENT_AUTO_REPLY.ENABLE.HELP') }}
          </p>
        </div>
        <woot-switch :model-value="enabled" @input="enabled = $event" />
      </div>
    </div>
  </SettingsSection>

  <SettingsSection
    v-if="isConnected && !justConnected"
    :title="$t('INBOX_MGMT.COMMENT_AUTO_REPLY.TEMPLATE.LABEL')"
    :sub-title="$t('INBOX_MGMT.COMMENT_AUTO_REPLY.TEMPLATE.HELP')"
  >
    <div class="flex flex-col gap-5">
      <div>
        <textarea
          v-model="template"
          rows="2"
          class="w-full"
          :placeholder="
            $t('INBOX_MGMT.COMMENT_AUTO_REPLY.TEMPLATE.PLACEHOLDER')
          "
        />
        <div class="flex items-center justify-between mt-1">
          <NextButton
            xs
            faded
            slate
            :label="$t('INBOX_MGMT.COMMENT_AUTO_REPLY.TEMPLATE.INSERT_NAME')"
            @click="insertNameVar"
          />
          <span class="text-xs text-n-slate-10">
            {{ $t('INBOX_MGMT.COMMENT_AUTO_REPLY.TEMPLATE.NO_LINKS') }}
          </span>
        </div>
      </div>

      <div>
        <p class="mb-2 text-sm font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.COMMENT_AUTO_REPLY.PREVIEW.TITLE') }}
        </p>
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div class="p-3 border rounded-lg bg-n-solid-1 border-n-weak">
            <p class="mb-3 text-xs text-n-slate-10">
              {{ $t('INBOX_MGMT.COMMENT_AUTO_REPLY.PREVIEW.PUBLIC_LABEL') }}
            </p>

            <div class="flex items-start gap-2">
              <div
                class="flex items-center justify-center flex-shrink-0 text-xs font-medium text-white rounded-full size-8 bg-n-slate-9"
              >
                {{
                  $t(
                    'INBOX_MGMT.COMMENT_AUTO_REPLY.PREVIEW.SAMPLE_AVATAR_INITIAL'
                  )
                }}
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-semibold text-n-slate-12">
                  {{ $t('INBOX_MGMT.COMMENT_AUTO_REPLY.PREVIEW.SAMPLE_NAME') }}
                </p>
                <p class="text-sm text-n-slate-12">
                  {{
                    $t('INBOX_MGMT.COMMENT_AUTO_REPLY.PREVIEW.SAMPLE_COMMENT')
                  }}
                </p>
                <div
                  class="flex items-center gap-3 mt-1 text-xs font-medium text-n-slate-10"
                >
                  <span>{{
                    $t('INBOX_MGMT.COMMENT_AUTO_REPLY.PREVIEW.LIKE')
                  }}</span>
                  <span>{{
                    $t('INBOX_MGMT.COMMENT_AUTO_REPLY.PREVIEW.REPLY_ACTION')
                  }}</span>
                  <span>{{
                    $t('INBOX_MGMT.COMMENT_AUTO_REPLY.PREVIEW.JUST_NOW')
                  }}</span>
                </div>
              </div>
            </div>

            <div class="flex items-start gap-2 mt-3 pl-9">
              <div
                class="flex items-center justify-center flex-shrink-0 text-xs font-medium text-white rounded-full size-7 bg-n-blue-9"
              >
                {{ pageAvatarInitial }}
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm">
                  <span class="font-semibold text-n-slate-12">{{
                    inbox.name
                  }}</span>
                  <span class="ml-1 text-xs text-n-slate-9">{{
                    $t('INBOX_MGMT.COMMENT_AUTO_REPLY.PREVIEW.AUTHOR_TAG')
                  }}</span>
                </p>
                <p class="text-sm text-n-slate-12">
                  <template
                    v-for="(part, index) in previewPublicReplyParts"
                    :key="index"
                  >
                    <span
                      v-if="part.isName"
                      class="font-medium text-n-blue-9"
                      >{{ part.text }}</span
                    >
                    <template v-else>{{ part.text }}</template>
                  </template>
                </p>
                <div
                  class="flex items-center gap-3 mt-1 text-xs font-medium text-n-slate-10"
                >
                  <span>{{
                    $t('INBOX_MGMT.COMMENT_AUTO_REPLY.PREVIEW.LIKE')
                  }}</span>
                  <span>{{
                    $t('INBOX_MGMT.COMMENT_AUTO_REPLY.PREVIEW.REPLY_ACTION')
                  }}</span>
                  <span>{{
                    $t('INBOX_MGMT.COMMENT_AUTO_REPLY.PREVIEW.JUST_NOW')
                  }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="p-3 border rounded-lg bg-n-solid-1 border-n-weak">
            <p class="mb-3 text-xs text-n-slate-10">
              {{ $t('INBOX_MGMT.COMMENT_AUTO_REPLY.PREVIEW.MESSENGER_LABEL') }}
            </p>
            <div class="flex justify-end">
              <div class="max-w-[85%] flex flex-col items-end gap-1">
                <span
                  class="inline-block px-2 py-0.5 text-[11px] rounded-full bg-n-blue-3 text-n-blue-11"
                >
                  {{ $t('INBOX_MGMT.COMMENT_AUTO_REPLY.PREVIEW.AI_BADGE') }}
                </span>
                <div
                  class="px-3 py-2 text-sm text-white rounded-2xl rounded-tr-sm bg-n-blue-9"
                >
                  {{
                    $t('INBOX_MGMT.COMMENT_AUTO_REPLY.PREVIEW.MESSENGER_MOCK')
                  }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <p class="mt-2 text-xs text-n-slate-10">
          {{ $t('INBOX_MGMT.COMMENT_AUTO_REPLY.PREVIEW.MESSENGER_HELP') }}
        </p>
      </div>

      <div>
        <NextButton
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
          :is-loading="isSaving"
          @click="save"
        />
      </div>
    </div>
  </SettingsSection>
</template>
